import * as THREE from 'three';
import { MaterialType, VoxelData, WeatherType } from './voxelTypes';
import { hexToColor } from './voxelColor';

export const VOXEL_SIZE = 0.5;

export interface VoxelBatch {
  opaque: VoxelData[];
  foliage: VoxelData[];
  emissive: VoxelData[];
  water: VoxelData[];
  glass: VoxelData[];
}

export class VoxelInstanceManager {
  private opaqueMesh: THREE.InstancedMesh | null = null;
  private foliageMesh: THREE.InstancedMesh | null = null;
  private emissiveMesh: THREE.InstancedMesh | null = null;
  private waterMesh: THREE.InstancedMesh | null = null;
  private glassMesh: THREE.InstancedMesh | null = null;

  private opaqueVoxels: VoxelData[] = [];
  private opaqueOriginalColors: THREE.Color[] = [];
  private foliageVoxels: VoxelData[] = [];
  private foliageOriginalColors: THREE.Color[] = [];

  private geometry = new THREE.BoxGeometry(VOXEL_SIZE * 1.002, VOXEL_SIZE * 1.002, VOXEL_SIZE * 1.002);
  private dummy = new THREE.Object3D();
  private scratchColor = new THREE.Color();

  public createInstancedWorld(
    scene: THREE.Scene,
    allVoxels: VoxelData[],
    homeLightPositions: { x: number; y: number; z: number }[] = []
  ): {
    drawCalls: number;
    pointLights: THREE.PointLight[];
  } {
    // Clear previous meshes
    this.dispose(scene);

    const batches: VoxelBatch = {
      opaque: [],
      foliage: [],
      emissive: [],
      water: [],
      glass: []
    };

    // Sort voxels into material batches
    allVoxels.forEach(v => {
      const type = v.materialType || 'opaque';
      if (type === 'foliage') batches.foliage.push(v);
      else if (type === 'emissive') batches.emissive.push(v);
      else if (type === 'water') batches.water.push(v);
      else if (type === 'glass') batches.glass.push(v);
      else batches.opaque.push(v);
    });

    const pointLights: THREE.PointLight[] = [];

    // Store references
    this.opaqueVoxels = batches.opaque;
    this.opaqueOriginalColors = batches.opaque.map(v =>
      typeof v.color === 'string' ? new THREE.Color(v.color) : hexToColor(v.color)
    );

    this.foliageVoxels = batches.foliage;
    this.foliageOriginalColors = batches.foliage.map(v =>
      typeof v.color === 'string' ? new THREE.Color(v.color) : hexToColor(v.color)
    );

    // 1. Opaque Voxel Instanced Mesh
    if (batches.opaque.length > 0) {
      const mat = new THREE.MeshStandardMaterial({
        roughness: 0.8,
        metalness: 0.1,
      });
      this.opaqueMesh = new THREE.InstancedMesh(this.geometry, mat, batches.opaque.length);
      this.populateMesh(this.opaqueMesh, batches.opaque, this.opaqueOriginalColors);
      this.opaqueMesh.castShadow = true;
      this.opaqueMesh.receiveShadow = true;
      scene.add(this.opaqueMesh);
    }

    // 2. Foliage Voxel Instanced Mesh
    if (batches.foliage.length > 0) {
      const mat = new THREE.MeshStandardMaterial({
        roughness: 0.88,
        metalness: 0.0,
      });
      this.foliageMesh = new THREE.InstancedMesh(this.geometry, mat, batches.foliage.length);
      this.populateMesh(this.foliageMesh, batches.foliage, this.foliageOriginalColors);
      this.foliageMesh.castShadow = true;
      this.foliageMesh.receiveShadow = true;
      scene.add(this.foliageMesh);
    }

    // 3. Emissive (Glow/Lamps/Windows) Instanced Mesh
    if (batches.emissive.length > 0) {
      const mat = new THREE.MeshBasicMaterial({
        toneMapped: false,
      });
      mat.onBeforeCompile = (shader) => {
        shader.uniforms.uEmissiveIntensity = { value: 3.5 };
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <common>',
          `#include <common>
          uniform float uEmissiveIntensity;
          `
        );
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <color_fragment>',
          `
          #include <color_fragment>
          diffuseColor.rgb *= uEmissiveIntensity;
          `
        );
        mat.userData.shader = shader;
      };
      this.emissiveMesh = new THREE.InstancedMesh(this.geometry, mat, batches.emissive.length);
      this.populateMeshEmissive(this.emissiveMesh, batches.emissive);
      scene.add(this.emissiveMesh);
    }

    // 4. Create warm incandescent lights for homes, lantern poles, windmill, and windows
    homeLightPositions.forEach(pos => {
      const light = new THREE.PointLight(0xffb05b, 4.0, 32);
      light.decay = 1.5;
      light.position.set(pos.x * VOXEL_SIZE, pos.y * VOXEL_SIZE, pos.z * VOXEL_SIZE);
      scene.add(light);
      pointLights.push(light);
    });

    // 4. Water Voxel Instanced Mesh (Cyan Shimmers)
    if (batches.water.length > 0) {
      const mat = new THREE.MeshPhysicalMaterial({
        color: 0xffffff,
        roughness: 0.08,
        transmission: 0.70,
        opacity: 0.88,
        transparent: true,
        ior: 1.333
      });
      this.waterMesh = new THREE.InstancedMesh(this.geometry, mat, batches.water.length);
      this.populateMesh(this.waterMesh, batches.water);
      this.waterMesh.receiveShadow = true;
      scene.add(this.waterMesh);
    }

    // 5. Glass Voxel Instanced Mesh (Windows)
    if (batches.glass.length > 0) {
      const mat = new THREE.MeshPhysicalMaterial({
        color: 0xffffff,
        roughness: 0.1,
        transmission: 0.9,
        opacity: 1,
        transparent: true,
        ior: 1.5
      });
      this.glassMesh = new THREE.InstancedMesh(this.geometry, mat, batches.glass.length);
      this.populateMesh(this.glassMesh, batches.glass);
      this.glassMesh.receiveShadow = true;
      scene.add(this.glassMesh);
    }

    let drawCalls = 0;
    if (this.opaqueMesh) drawCalls++;
    if (this.foliageMesh) drawCalls++;
    if (this.emissiveMesh) drawCalls++;
    if (this.waterMesh) drawCalls++;
    if (this.glassMesh) drawCalls++;

    return { drawCalls, pointLights };
  }

  public setEmissiveIntensity(intensity: number) {
    if (this.emissiveMesh && this.emissiveMesh.material) {
      const mat = this.emissiveMesh.material as THREE.MeshBasicMaterial;
      if (mat.userData.shader && mat.userData.shader.uniforms && mat.userData.shader.uniforms.uEmissiveIntensity) {
        mat.userData.shader.uniforms.uEmissiveIntensity.value = intensity;
      }
    }
  }

  private populateMesh(mesh: THREE.InstancedMesh, voxels: VoxelData[], colors?: THREE.Color[]) {
    voxels.forEach((v, i) => {
      this.dummy.position.set(v.x * VOXEL_SIZE, v.y * VOXEL_SIZE, v.z * VOXEL_SIZE);
      const sx = v.scaleX ?? v.scale ?? 1;
      const sy = v.scaleY ?? v.scale ?? 1;
      const sz = v.scaleZ ?? v.scale ?? 1;
      this.dummy.scale.set(sx, sy, sz);
      this.dummy.updateMatrix();
      mesh.setMatrixAt(i, this.dummy.matrix);

      const color = colors && colors[i]
        ? colors[i]
        : (typeof v.color === 'string' ? new THREE.Color(v.color) : hexToColor(v.color));
      mesh.setColorAt(i, color);
    });
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }

  public updateWeatherColors(weather: WeatherType) {
    const snowyGrassColor = new THREE.Color(0xf2f7fc);
    const snowyFoliageColor = new THREE.Color(0xe2f0fd);
    const wetDarkFactor = 0.82;

    // This walks every voxel in the village, so the tinted colour is written into one scratch
    // instance rather than allocating two per voxel.
    const tinted = this.scratchColor;

    if (this.opaqueMesh && this.opaqueMesh.instanceColor && this.opaqueVoxels.length > 0) {
      for (let i = 0; i < this.opaqueVoxels.length; i++) {
        const v = this.opaqueVoxels[i]!;
        const origColor = this.opaqueOriginalColors[i]!;

        if (weather === 'snow') {
          // Check if voxel is grass, dirt top, thatch, or upper terrain surface
          const isGreen = origColor.g > origColor.r && origColor.g > origColor.b;
          const isWarmOchre = origColor.r > 0.45 && origColor.g > 0.35 && origColor.b < 0.45;
          const isUpper = v.y >= 1;

          if (isGreen || (isWarmOchre && isUpper)) {
            this.opaqueMesh.setColorAt(i, snowyGrassColor);
          } else {
            this.opaqueMesh.setColorAt(i, tinted.copy(origColor).lerp(snowyGrassColor, 0.45));
          }
        } else if (weather === 'rain' || weather === 'storm') {
          this.opaqueMesh.setColorAt(i, tinted.copy(origColor).multiplyScalar(wetDarkFactor));
        } else {
          this.opaqueMesh.setColorAt(i, origColor);
        }
      }
      this.opaqueMesh.instanceColor.needsUpdate = true;
    }

    if (this.foliageMesh && this.foliageMesh.instanceColor && this.foliageVoxels.length > 0) {
      for (let i = 0; i < this.foliageVoxels.length; i++) {
        const origColor = this.foliageOriginalColors[i]!;
        if (weather === 'snow') {
          this.foliageMesh.setColorAt(i, snowyFoliageColor);
        } else if (weather === 'rain' || weather === 'storm') {
          this.foliageMesh.setColorAt(i, tinted.copy(origColor).multiplyScalar(wetDarkFactor));
        } else {
          this.foliageMesh.setColorAt(i, origColor);
        }
      }
      this.foliageMesh.instanceColor.needsUpdate = true;
    }
  }

  private populateMeshEmissive(mesh: THREE.InstancedMesh, voxels: VoxelData[]) {
    voxels.forEach((v, i) => {
      this.dummy.position.set(v.x * VOXEL_SIZE, v.y * VOXEL_SIZE, v.z * VOXEL_SIZE);
      const sx = v.scaleX ?? v.scale ?? 1;
      const sy = v.scaleY ?? v.scale ?? 1;
      const sz = v.scaleZ ?? v.scale ?? 1;
      this.dummy.scale.set(sx, sy, sz);
      this.dummy.updateMatrix();
      mesh.setMatrixAt(i, this.dummy.matrix);

      const color = typeof v.color === 'string' ? new THREE.Color(v.color) : hexToColor(v.color);
      mesh.setColorAt(i, color);
    });
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }

  public animateWater(time: number) {
    if (!this.waterMesh) return;

    const count = this.waterMesh.count;
    const matrix = new THREE.Matrix4();
    const position = new THREE.Vector3();
    const scale = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();

    for (let i = 0; i < count; i++) {
      this.waterMesh.getMatrixAt(i, matrix);
      matrix.decompose(position, quaternion, scale);

      const origY = Math.round(position.y / VOXEL_SIZE) * VOXEL_SIZE;
      const wave = Math.sin(time * 2.5 + position.x * 0.4 + position.z * 0.4) * (0.08 * VOXEL_SIZE);
      position.y = origY + wave;

      matrix.compose(position, quaternion, scale);
      this.waterMesh.setMatrixAt(i, matrix);
    }
    this.waterMesh.instanceMatrix.needsUpdate = true;
  }

  public dispose(scene: THREE.Scene) {
    [this.opaqueMesh, this.foliageMesh, this.emissiveMesh, this.waterMesh, this.glassMesh].forEach(mesh => {
      if (mesh) {
        scene.remove(mesh);
        mesh.geometry.dispose();
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach(m => m.dispose());
        } else {
          mesh.material.dispose();
        }
      }
    });
    this.opaqueMesh = null;
    this.foliageMesh = null;
    this.emissiveMesh = null;
    this.waterMesh = null;
    this.glassMesh = null;
  }
}
